import React from "react";
import { createRoot } from "react-dom/client";
import { setupIonicReact } from "@ionic/react";
import { registerSW } from "virtual:pwa-register";

import App from "./App";
import { setSwRegistration } from "./lib/device";
import { announceUpdate, pollForUpdates, setUpdater } from "./lib/swUpdate";
import { useKaya } from "./store/kayaStore";

/* Ionic base styles, then the ported Kaya design system on top. */
import "@ionic/react/css/core.css";
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";
import "./theme/fonts.css";
import "./theme/kaya.css";
import "./theme/ionic-bridge.css";

setupIonicReact({ mode: "md", swipeBackEnabled: false });

const updateSW = registerSW({
  immediate: true,
  onRegisteredSW(_url, reg) {
    setSwRegistration(reg || null);
    pollForUpdates(reg, swapAtLaunch);
  },
  onNeedRefresh() {
    // A build is installed and waiting. Ask rather than swap: a reload mid-session would lose a
    // running session or treadmill timer.
    announceUpdate();
  },
});
setUpdater(updateSW);

/** A build already waiting when Kaya opens takes over at once — nothing has been started — unless
 *  a session is open on this phone, when it is offered on Today instead (lib/swUpdate.ts). The
 *  session record is in LocalMeta, so this waits for the store to load it. */
async function swapAtLaunch(): Promise<boolean> {
  await new Promise<void>((done) => {
    if (useKaya.getState().ready) return done();
    const stop = useKaya.subscribe((s) => {
      if (s.ready) {
        stop();
        done();
      }
    });
  });
  return useKaya.getState().session === null;
}

// Load persisted state before the first render so screens never flash empty defaults.
void useKaya.getState().init();

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
